import React, { useCallback, useEffect, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIconProps
} from "@mui/material";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";

export enum MeasureFilterMode {
  GreaterThan = "greaterThan",
  LessThan = "lessThan",
  Equal = "equal"
}

const MEASURE_FILTER_MODES: { mode: MeasureFilterMode; label: string; Icon: React.ComponentType<SvgIconProps> }[] = [
  { mode: MeasureFilterMode.GreaterThan, label: "Greater than", Icon: ChevronRightIcon },
  { mode: MeasureFilterMode.LessThan, label: "Less than", Icon: ChevronLeftIcon },
  { mode: MeasureFilterMode.Equal, label: "Equal to", Icon: DragHandleIcon }
];

export const getMeasureFilterMode = (filter?: { from_value?: number; to_value?: number }): MeasureFilterMode => {
  if (!filter) return MeasureFilterMode.GreaterThan;
  if (filter.from_value !== undefined && filter.to_value !== undefined) return MeasureFilterMode.Equal;
  if (filter.to_value !== undefined) return MeasureFilterMode.LessThan;
  return MeasureFilterMode.GreaterThan;
};

export const getMeasureFilterValue = (filter?: { from_value?: number; to_value?: number }): number | "" => {
  if (!filter) return "";
  return filter.from_value ?? filter.to_value ?? "";
};

interface IMeasureInputProps {
  columnTitle: string;
  columnKey: string;
  value: number | "";
  mode: MeasureFilterMode;
  onChange: (key: string, value: number | "", mode: MeasureFilterMode) => void;
  container?: Element | null;
}

export const MeasureInput: React.FC<IMeasureInputProps> = React.memo(
  ({ columnTitle, columnKey, value, mode, onChange, container }) => {
    const [localValue, setLocalValue] = useState<number | "">(value);
    const [filterMode, setFilterMode] = useState<MeasureFilterMode>(mode);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    useEffect(() => {
      setLocalValue(value);
      setFilterMode(mode);
    }, [value, mode]);

    const submitChange = useCallback(
      (submitValue: number | "", submitMode: MeasureFilterMode) => {
        onChange(columnKey, submitValue, submitMode);
      },
      [columnKey, onChange]
    );

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocalValue(raw === "" ? "" : Number(raw));
    }, []);

    const handleBlur = useCallback(() => {
      if (localValue === value && filterMode === mode) return;
      submitChange(localValue, filterMode);
    }, [localValue, filterMode, submitChange, value, mode]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        if (localValue === value && filterMode === mode) return;
        submitChange(localValue, filterMode);
      },
      [localValue, filterMode, submitChange, value, mode]
    );

    const handleModeSelect = useCallback(
      (selectedMode: MeasureFilterMode) => {
        setFilterMode(selectedMode);
        setAnchorEl(null);
        if (localValue === "") return;

        submitChange(localValue, selectedMode);
      },
      [localValue, submitChange]
    );

    const currentModeConfig = MEASURE_FILTER_MODES.find((item) => item.mode === filterMode)!;

    return (
      <>
        <NumberInput
          size="small"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={columnTitle}
          variant="outlined"
          sx={{
            "& .MuiInputBase-root": { fontSize: "14px" },
            "& .MuiInputBase-input": { height: 16, padding: "8px 8px 8px 4px" }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ marginRight: 0 }}>
                <SearchIcon sx={{ fontSize: 16, color: "#0a3142" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: 0 }}>
                <MaterialTooltip title="Select filter option" isShow>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <IconButton
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      sx={{ padding: "2px", width: 24, height: 24 }}
                    >
                      <currentModeConfig.Icon sx={{ fontSize: 16, color: "#0a3142" }} />
                    </IconButton>
                  </Box>
                </MaterialTooltip>
              </InputAdornment>
            )
          }}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          container={container ?? document.body}
          slotProps={{ paper: { sx: { minWidth: 160, mt: "4px" } } }}
        >
          {MEASURE_FILTER_MODES.map(({ mode: modeOption, label, Icon }) => (
            <MenuItem
              key={modeOption}
              selected={modeOption === filterMode}
              onClick={() => handleModeSelect(modeOption)}
              sx={{ fontSize: 14 }}
            >
              <ListItemIcon sx={{ minWidth: "28px !important" }}>
                <Icon sx={{ fontSize: 18, color: modeOption === filterMode ? "primary.main" : "#5d7782" }} />
              </ListItemIcon>
              <ListItemText slotProps={{ primary: { fontSize: 14 } }}>{label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }
);
